import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  TrendingUp, TrendingDown, IndianRupee, PieChart, 
  BarChart3, Calendar, ArrowUpRight, ArrowDownRight,
  RefreshCw, Award, Target, Zap, Sparkles, Info, HelpCircle,
  Camera, Clock
} from 'lucide-react';
import { api } from '../App';

// Quality name mapping from Excel file names to display names
const QUALITY_MAP = {
  'NStock-Low': 'LOW',
  'NStock- V Low': 'V LOW',
  'NStock- VV LOW': 'VV LOW',
  'NStock-Moderate': 'MODERATE',
  'NStock-High': 'HIGH',
  'NStock-New': 'NEW',
  'NStock-V Low': 'V LOW',
  'NStock-VV Low': 'VV LOW',
  'Nstock-Moderate': 'MODERATE',
  'Stock-V. Low': 'V LOW',
  'Stock-V V Low': 'VV LOW',
  'New_Shares': 'NEW',
};

const mapQualityName = (name) => {
  if (!name) return 'Unknown';
  if (QUALITY_MAP[name]) return QUALITY_MAP[name];
  const lowerName = name.toLowerCase();
  if (lowerName.includes('vv low') || lowerName.includes('v v low')) return 'VV LOW';
  if (lowerName.includes('v low') || lowerName.includes('v. low')) return 'V LOW';
  if (lowerName.includes('low')) return 'LOW';
  if (lowerName.includes('moderate')) return 'MODERATE';
  if (lowerName.includes('high')) return 'HIGH';
  if (lowerName.includes('new')) return 'NEW';
  return name;
};

const Performance = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [summary, setSummary] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [filteredSnapshots, setFilteredSnapshots] = useState([]);
  const [dividendData, setDividendData] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('year');
  
  // Modal states for calculation details
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailType, setDetailType] = useState(null);
  
  // Market status and daily snapshot
  const [marketStatus, setMarketStatus] = useState(null);
  const [todaySnapshot, setTodaySnapshot] = useState(null);
  const [capturingSnapshot, setCapturingSnapshot] = useState(false);
  
  // Quality analysis data
  const [qualityData, setQualityData] = useState(null);
  const [loadingQuality, setLoadingQuality] = useState(false);
  
  // Value range detail modal
  const [selectedRange, setSelectedRange] = useState(null);
  const [showRangeModal, setShowRangeModal] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, []);

  // Filter snapshots based on selected period
  useEffect(() => {
    if (snapshots.length === 0) {
      setFilteredSnapshots([]);
      return;
    }
    
    const today = new Date();
    let cutoffDate = new Date();
    
    switch (selectedPeriod) {
      case 'day':
        cutoffDate.setDate(today.getDate() - 1);
        break;
      case 'week':
        cutoffDate.setDate(today.getDate() - 7);
        break;
      case 'month':
        cutoffDate.setMonth(today.getMonth() - 1);
        break;
      case 'year':
        cutoffDate.setFullYear(today.getFullYear() - 1);
        break;
      case 'all':
      default:
        cutoffDate = new Date(2000, 0, 1);
        break;
    }
    
    const filtered = snapshots.filter(snap => {
      const snapDate = new Date(snap.date);
      // Filter out future dates and dates before cutoff
      return snapDate <= today && snapDate >= cutoffDate;
    });
    
    setFilteredSnapshots(filtered);
  }, [snapshots, selectedPeriod]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [summaryRes, snapshotsRes, dividendRes, investmentsRes, marketRes, todayRes] = await Promise.all([
        api.get('/performance/summary'),
        api.get('/performance/snapshots'),
        api.get('/performance/dividend-performance'),
        api.get('/performance/value-investments'),
        api.get('/market/status'),
        api.get('/snapshots/today')
      ]);
      setSummary(summaryRes.data);
      setMarketStatus(marketRes.data);
      setTodaySnapshot(todayRes.data);
      
      // Filter out future dates and invalid dates (silently - future dates are projections)
      const today = new Date();
      const validSnapshots = (snapshotsRes.data || []).filter(snap => {
        const snapDate = new Date(snap.date);
        // Keep only records up to today with valid years
        return snapDate <= today && snapDate.getFullYear() >= 2000;
      });
      
      setSnapshots(validSnapshots);
      setDividendData(dividendRes.data);
      setInvestments(investmentsRes.data);
    } catch (err) {
      console.error('Failed to fetch performance data');
    } finally {
      setLoading(false);
    }
  };

  // Fetch quality analysis data when tab is selected
  const fetchQualityData = async () => {
    if (qualityData) return; // Already loaded
    setLoadingQuality(true);
    try {
      const res = await api.get('/performance/gains-by-quality');
      setQualityData(res.data);
    } catch (err) {
      console.error('Failed to fetch quality data:', err);
      toast.error('Failed to load quality analysis');
    } finally {
      setLoadingQuality(false);
    }
  };

  // Load quality data when switching to that tab
  useEffect(() => {
    if (activeTab === 'quality') {
      fetchQualityData();
    }
  }, [activeTab]);

  const handleCaptureSnapshot = async () => {
    setCapturingSnapshot(true);
    try {
      const res = await api.post('/snapshots/capture-daily');
      if (res.data.status === 'captured') {
        toast.success('Daily snapshot captured!', {
          description: `Portfolio: ${formatCurrency(res.data.portfolio_value)} (${res.data.change_percent >= 0 ? '+' : ''}${res.data.change_percent}%)`
        });
        fetchAllData(); // Refresh all data
      } else if (res.data.status === 'exists') {
        toast.info('Snapshot already exists for today');
      }
    } catch (err) {
      console.error('Failed to capture snapshot:', err);
      toast.error('Failed to capture snapshot');
    } finally {
      setCapturingSnapshot(false);
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '₹0';
    if (amount >= 10000000) {
      return `₹${(amount / 10000000).toFixed(2)} Cr`;
    } else if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(2)} L`;
    }
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined) return '0%';
    return `${value >= 0 ? '+' : ''}${value?.toFixed(2) || 0}%`;
  };

  const showCalculationDetail = (type) => {
    setDetailType(type);
    setShowDetailModal(true);
  };

  const getCalculationDetails = () => {
    if (!summary) return null;
    
    const details = {
      portfolio_value: {
        title: 'Portfolio Value Calculation',
        formula: 'Sum of (Current Price × Quantity) for all holdings',
        breakdown: [
          { label: 'Total Market Value', value: formatCurrency(summary.total_market_value), source: 'Holdings × Current Prices' },
        ],
        dataSource: 'Cheatsheet.xlsx → Value & Investments tab',
        explanation: 'This is the current market value of all your holdings calculated by multiplying the quantity of each stock by its current market price.'
      },
      total_invested: {
        title: 'Total Invested Calculation',
        formula: 'Sum of (Purchase Price × Quantity) for all holdings',
        breakdown: [
          { label: 'Total Amount Invested', value: formatCurrency(summary.total_invested), source: 'Holdings × Purchase Prices' },
        ],
        dataSource: 'Cheatsheet.xlsx → Value & Investments tab',
        explanation: 'This is the total amount you have invested across all holdings, calculated using your original purchase prices.'
      },
      total_gain: {
        title: 'Total Gain Calculation',
        formula: 'Portfolio Value - Total Invested',
        breakdown: [
          { label: 'Portfolio Value', value: formatCurrency(summary.total_market_value), source: 'Current market value' },
          { label: 'Total Invested', value: `- ${formatCurrency(summary.total_invested)}`, source: 'Original investment' },
          { label: 'Total Gain', value: formatCurrency(summary.total_gain), source: 'Difference', highlight: true },
          { label: 'Gain Percentage', value: formatPercent(summary.total_gain_percent), source: '(Gain ÷ Invested) × 100' },
        ],
        dataSource: 'Cheatsheet.xlsx → Value & Investments tab',
        explanation: 'The unrealized profit/loss on your investments. This is the difference between what your holdings are worth now vs what you paid for them.'
      },
      net_return: {
        title: 'Net Return Calculation',
        formula: 'Total Gain + Dividends Received - Interest Paid',
        breakdown: [
          { label: 'Total Gain (Unrealized)', value: formatCurrency(summary.total_gain), source: 'Portfolio appreciation' },
          { label: 'Dividends Received', value: `+ ${formatCurrency(summary.total_dividends)}`, source: 'All dividend payments' },
          { label: 'Interest Paid', value: `- ${formatCurrency(summary.total_interest_paid)}`, source: 'Loan borrowing costs' },
          { label: 'Net Return', value: formatCurrency(summary.net_return), source: 'Final net profit/loss', highlight: true },
        ],
        dataSource: 'Multiple sources: Value & Investments, Interest tab, Dividend data',
        explanation: 'Your true return after accounting for all income (dividends) and costs (interest on loans). This gives a complete picture of your investment performance.'
      },
      dividends: {
        title: 'Dividends Earned',
        formula: 'Sum of all dividend payments received',
        breakdown: [
          { label: 'Total Dividends', value: formatCurrency(summary.total_dividends), source: 'All dividend records' },
          { label: 'Dividend Yield', value: summary.total_invested > 0 ? `${(summary.total_dividends / summary.total_invested * 100).toFixed(2)}%` : '0%', source: '(Dividends ÷ Invested) × 100' },
        ],
        dataSource: 'Cheatsheet.xlsx → Dividend data or separate dividend uploads',
        explanation: 'Total cash received as dividends from your stock holdings.'
      },
      interest: {
        title: 'Interest Paid',
        formula: 'Sum of all interest payments on loans (LAS/MTF)',
        breakdown: [
          { label: 'Total Interest', value: formatCurrency(summary.total_interest_paid), source: 'Monthly interest records' },
        ],
        dataSource: 'Cheatsheet.xlsx → Interest tab (monthly snapshots)',
        explanation: 'Total interest paid on borrowed funds (Loan Against Shares, Margin Trading Facility). This is a cost that reduces your net returns.'
      }
    };
    
    return details[detailType] || null;
  };

  const getInsights = () => {
    if (!summary || snapshots.length === 0) return [];
    
    const insights = [];
    
    if (summary.total_gain_percent > 0) {
      insights.push({
        icon: TrendingUp,
        color: 'green',
        title: 'Portfolio Growing! 🎉',
        message: `Your investments have grown ${summary.total_gain_percent.toFixed(1)}% overall.`
      });
    }
    
    if (summary.total_dividends > 0) {
      const dividendYield = (summary.total_dividends / summary.total_invested * 100).toFixed(2);
      insights.push({
        icon: IndianRupee,
        color: 'blue',
        title: `Dividend Income: ${formatCurrency(summary.total_dividends)}`,
        message: `Effective yield of ${dividendYield}% on your investments.`
      });
    }
    
    if (summary.net_return > 0) {
      insights.push({
        icon: Award,
        color: 'purple',
        title: 'Net Profit! 💪',
        message: `After all costs, your net return is ${formatCurrency(summary.net_return)}`
      });
    }
    
    return insights;
  };

  const getBestCategory = () => {
    if (investments.length === 0) return null;
    return investments.reduce((best, curr) => 
      curr.gain_percent > (best?.gain_percent || -Infinity) ? curr : best, null);
  };

  // Get the latest valid date from snapshots
  const getLatestDataDate = () => {
    if (filteredSnapshots.length === 0) return null;
    const sorted = [...filteredSnapshots].sort((a, b) => new Date(b.date) - new Date(a.date));
    return sorted[0]?.date;
  };

  // Calculate period metrics with reference to today
  const getPeriodMetrics = () => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    
    if (filteredSnapshots.length === 0) {
      return {
        noData: true,
        period: selectedPeriod,
        message: `No data available for the selected ${selectedPeriod} period.`
      };
    }
    
    const sorted = [...filteredSnapshots].sort((a, b) => new Date(a.date) - new Date(b.date));
    const firstSnapshot = sorted[0];
    const lastSnapshot = sorted[sorted.length - 1];
    
    const startValue = Number(firstSnapshot.portfolio_value) || 0;
    const endValue = Number(lastSnapshot.portfolio_value) || 0;
    const valueChange = endValue - startValue;
    const percentChange = startValue > 0
      ? (valueChange / startValue * 100)
      : 0;
    
    // Check if there's no change (for funny note)
    const noChange = Math.abs(valueChange) < 1 && filteredSnapshots.length <= 1;
    
    // Generate period description
    let periodDescription = '';
    let funnyNote = '';
    
    switch (selectedPeriod) {
      case 'day':
        periodDescription = `Since yesterday (${new Date(Date.now() - 86400000).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })})`;
        if (noChange || filteredSnapshots.length <= 1) {
          const funnyNotes = [
            "🏖️ Looks like the markets took a day off! Even stocks need a vacation sometimes.",
            "☕ No movement today - the market is having a coffee break!",
            "🦥 The stock market is channeling its inner sloth today. Slow and steady!",
            "🎯 Zero change! Either it's a holiday, or you've achieved perfect market zen.",
            "😴 Markets are napping. Wake them up tomorrow!",
            "🌴 No trading today? Must be a holiday... or the universe is testing your patience.",
          ];
          funnyNote = funnyNotes[Math.floor(Math.random() * funnyNotes.length)];
        }
        break;
      case 'week':
        periodDescription = `Last 7 days (since ${new Date(Date.now() - 7 * 86400000).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })})`;
        if (noChange || filteredSnapshots.length <= 1) {
          funnyNote = "📊 A whole week with minimal change? The market is playing hard to get!";
        }
        break;
      case 'month':
        periodDescription = `Last 30 days (since ${new Date(Date.now() - 30 * 86400000).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })})`;
        break;
      case 'year':
        periodDescription = `Last 12 months (since ${new Date(Date.now() - 365 * 86400000).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })})`;
        break;
      case 'all':
        periodDescription = `All time (from ${new Date(firstSnapshot.date).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })})`;
        break;
      default:
        periodDescription = '';
    }
    
    return {
      noData: false,
      startDate: firstSnapshot.date,
      endDate: lastSnapshot.date,
      startValue,
      endValue,
      valueChange,
      percentChange,
      dataPoints: filteredSnapshots.length,
      periodDescription,
      funnyNote,
      noChange: noChange || filteredSnapshots.length <= 1
    };
  };

  if (loading) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="animate-spin text-primary" size={32} />
        </div>
      </Layout>
    );
  }

  const insights = getInsights();
  const bestCategory = getBestCategory();
  const calculationDetail = getCalculationDetails();
  const latestDataDate = getLatestDataDate();
  const periodMetrics = getPeriodMetrics();

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6">
        {/* Header with Date Context */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
              <BarChart3 className="text-primary flex-shrink-0" size={22} />
              <span>Performance Analytics</span>
            </h1>
            <p className="text-xs sm:text-sm text-gray-600">
              <span className="block sm:inline">Today: <span className="font-medium">{new Date().toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}</span></span>
              {latestDataDate && (
                <span className="block sm:inline sm:ml-1"> • Latest Data: <span className="font-medium text-primary">{new Date(latestDataDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span></span>
              )}
            </p>
          </div>
          <Button onClick={fetchAllData} variant="outline" size="sm" className="w-full sm:w-auto">
            <RefreshCw size={16} className="mr-2" />
            Refresh
          </Button>
        </div>

        {/* Market Status & Daily Snapshot */}
        {marketStatus && (
          <div className={`p-3 sm:p-4 rounded-lg border ${
            marketStatus.is_open 
              ? 'bg-green-50 border-green-200' 
              : 'bg-amber-50 border-amber-200'
          }`}>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className={`p-2 rounded-full flex-shrink-0 ${marketStatus.is_open ? 'bg-green-100' : 'bg-amber-100'}`}>
                  {marketStatus.is_open ? (
                    <TrendingUp className="text-green-600" size={18} />
                  ) : (
                    <Clock className="text-amber-600" size={18} />
                  )}
                </div>
                <div>
                  <p className="font-semibold text-sm sm:text-base text-gray-800">
                    {marketStatus.is_open ? 'Markets Open' : 'Markets Closed'}
                  </p>
                  <p className="text-xs sm:text-sm text-gray-600">{marketStatus.message}</p>
                  {marketStatus.holiday_name && (
                    <p className="text-xs text-amber-700">Holiday: {marketStatus.holiday_name}</p>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-3 w-full sm:w-auto">
                {todaySnapshot?.has_snapshot ? (
                  <div className="text-left sm:text-right">
                    <p className="text-xs text-gray-500">Today&apos;s Snapshot Captured</p>
                    <p className="text-sm font-medium text-green-600">
                      {formatCurrency(todaySnapshot.snapshot?.portfolio_value)}
                      <span className={`ml-2 ${todaySnapshot.snapshot?.net_increase >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        ({formatPercent(todaySnapshot.snapshot?.net_increase_percent)})
                      </span>
                    </p>
                  </div>
                ) : marketStatus.is_open ? (
                  <Button
                    onClick={handleCaptureSnapshot}
                    disabled={capturingSnapshot}
                    size="sm"
                    className="bg-primary hover:bg-primary/90"
                  >
                    {capturingSnapshot ? (
                      <>
                        <RefreshCw className="animate-spin mr-2" size={16} />
                        Capturing...
                      </>
                    ) : (
                      <>
                        <Camera size={16} className="mr-2" />
                        Track Today&apos;s Performance
                      </>
                    )}
                  </Button>
                ) : (
                  <p className="text-sm text-gray-500 italic">
                    {marketStatus.next_open ? `Next trading: ${marketStatus.next_open}` : 'Check back next trading day'}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Period Selector */}
        <div className="flex gap-2 flex-wrap">
          {[
            { id: 'day', label: 'Day' },
            { id: 'week', label: 'Week' },
            { id: 'month', label: 'Month' },
            { id: 'year', label: 'Year' },
            { id: 'all', label: 'All Time' },
          ].map(period => (
            <Button
              key={period.id}
              variant={selectedPeriod === period.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedPeriod(period.id)}
            >
              {period.label}
            </Button>
          ))}
        </div>

        {/* Period Summary */}
        {periodMetrics && (
          <div className="card bg-gradient-to-r from-gray-50 to-slate-50 border">
            {periodMetrics.noData ? (
              <div className="text-center py-4">
                <p className="text-gray-500">{periodMetrics.message}</p>
              </div>
            ) : (
              <>
                <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center justify-between gap-3 sm:gap-4">
                  <div className="w-full sm:w-auto">
                    <p className="text-sm font-medium text-gray-700">
                      {selectedPeriod.charAt(0).toUpperCase() + selectedPeriod.slice(1)} Performance
                    </p>
                    <p className="text-xs text-gray-500">
                      {periodMetrics.periodDescription} • {periodMetrics.dataPoints} data point{periodMetrics.dataPoints !== 1 ? 's' : ''}
                    </p>
                    <p className="text-xs text-gray-400">
                      {new Date(periodMetrics.startDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} → {new Date(periodMetrics.endDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-4 sm:gap-6 w-full sm:w-auto">
                    <div className="text-left sm:text-center">
                      <p className="text-xs text-gray-500">Start Value</p>
                      <p className="font-medium text-sm sm:text-base">{formatCurrency(periodMetrics.startValue)}</p>
                    </div>
                    <div className="text-left sm:text-center">
                      <p className="text-xs text-gray-500">Current Value</p>
                      <p className="font-medium text-sm sm:text-base">{formatCurrency(periodMetrics.endValue)}</p>
                    </div>
                    <div className="text-left sm:text-center">
                      <p className="text-xs text-gray-500">Change</p>
                      <p className={`font-bold text-sm sm:text-base ${periodMetrics.valueChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {periodMetrics.valueChange >= 0 ? '+' : ''}{formatCurrency(periodMetrics.valueChange)}
                      </p>
                      <p className={`text-xs ${periodMetrics.percentChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatPercent(periodMetrics.percentChange)}
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Funny note for no change */}
                {periodMetrics.funnyNote && (
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-700">{periodMetrics.funnyNote}</p>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex gap-1 sm:gap-2 border-b pb-2 overflow-x-auto scrollbar-hide">
          {[
            { id: 'overview', label: 'Overview', icon: Sparkles },
            { id: 'quality', label: 'Gains by Quality', icon: BarChart3 },
            { id: 'investments', label: 'By Quality', icon: PieChart },
            { id: 'interest', label: 'Monthly History', icon: TrendingDown },
            { id: 'dividends', label: 'Dividends', icon: IndianRupee },
          ].map(tab => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? 'default' : 'ghost'}
              onClick={() => setActiveTab(tab.id)}
              className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2"
            >
              <tab.icon size={14} className="flex-shrink-0" />
              <span className="hidden xs:inline sm:inline">{tab.label}</span>
              <span className="xs:hidden sm:hidden">{tab.label.split(' ')[0]}</span>
            </Button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-4 sm:space-y-6">
            {/* Key Metrics - Interactive Cards */}
            {summary && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                <div 
                  className="card bg-gradient-to-br from-green-50 to-green-100 border border-green-200 cursor-pointer hover:shadow-lg transition-all p-3 sm:p-4"
                  onClick={() => showCalculationDetail('portfolio_value')}
                >
                  <div className="flex items-center justify-between">
                    <div className="min-w-0">
                      <p className="text-xs sm:text-sm text-green-700 flex items-center gap-1">
                        Portfolio Value <Info size={14} className="text-green-500" />
                      </p>
                      <p className="text-2xl font-bold text-green-800">
                        {formatCurrency(summary.total_market_value)}
                      </p>
                      <p className="text-xs text-green-600 mt-1">Click for details</p>
                    </div>
                    <div className="p-3 bg-green-500 rounded-full">
                      <TrendingUp className="text-white" size={24} />
                    </div>
                  </div>
                </div>

                <div 
                  className="card bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 cursor-pointer hover:shadow-lg transition-all"
                  onClick={() => showCalculationDetail('total_invested')}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-blue-700 flex items-center gap-1">
                        Total Invested <Info size={14} className="text-blue-500" />
                      </p>
                      <p className="text-2xl font-bold text-blue-800">
                        {formatCurrency(summary.total_invested)}
                      </p>
                      <p className="text-xs text-blue-600 mt-1">Click for details</p>
                    </div>
                    <div className="p-3 bg-blue-500 rounded-full">
                      <IndianRupee className="text-white" size={24} />
                    </div>
                  </div>
                </div>

                <div 
                  className="card bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 cursor-pointer hover:shadow-lg transition-all"
                  onClick={() => showCalculationDetail('total_gain')}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-purple-700 flex items-center gap-1">
                        Total Gain <Info size={14} className="text-purple-500" />
                      </p>
                      <p className="text-2xl font-bold text-purple-800">
                        {formatCurrency(summary.total_gain)}
                      </p>
                      <p className={`text-sm ${summary.total_gain_percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatPercent(summary.total_gain_percent)}
                      </p>
                    </div>
                    <div className="p-3 bg-purple-500 rounded-full">
                      {summary.total_gain >= 0 ? 
                        <ArrowUpRight className="text-white" size={24} /> :
                        <ArrowDownRight className="text-white" size={24} />
                      }
                    </div>
                  </div>
                </div>

                <div 
                  className="card bg-gradient-to-br from-amber-50 to-amber-100 border border-amber-200 cursor-pointer hover:shadow-lg transition-all"
                  onClick={() => showCalculationDetail('net_return')}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-amber-700 flex items-center gap-1">
                        Net Return <Info size={14} className="text-amber-500" />
                      </p>
                      <p className="text-2xl font-bold text-amber-800">
                        {formatCurrency(summary.net_return)}
                      </p>
                      <p className="text-xs text-amber-600">Click for calculation</p>
                    </div>
                    <div className="p-3 bg-amber-500 rounded-full">
                      <Award className="text-white" size={24} />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Additional Metrics Row */}
            {summary && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div 
                  className="card border cursor-pointer hover:shadow-lg transition-all"
                  onClick={() => showCalculationDetail('dividends')}
                >
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-blue-100 rounded-full">
                      <IndianRupee className="text-blue-600" size={24} />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-600 flex items-center gap-1">
                        Dividends Earned <Info size={14} className="text-gray-400" />
                      </p>
                      <p className="text-xl font-bold text-blue-700">{formatCurrency(summary.total_dividends)}</p>
                      <p className="text-sm text-gray-500">
                        Yield: {summary.total_invested > 0 ? `${(summary.total_dividends / summary.total_invested * 100).toFixed(2)}%` : '0%'}
                      </p>
                    </div>
                  </div>
                </div>

                <div 
                  className="card border cursor-pointer hover:shadow-lg transition-all"
                  onClick={() => showCalculationDetail('interest')}
                >
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-red-100 rounded-full">
                      <TrendingDown className="text-red-600" size={24} />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-600 flex items-center gap-1">
                        Interest Paid <Info size={14} className="text-gray-400" />
                      </p>
                      <p className="text-xl font-bold text-red-700">{formatCurrency(summary.total_interest_paid)}</p>
                      <p className="text-sm text-gray-500">Borrowing cost (LAS/MTF)</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Insights Section */}
            {insights.length > 0 && (
              <div className="card">
                <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                  <Zap className="text-yellow-500" />
                  Key Insights
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {insights.map((insight, idx) => (
                    <div key={idx} className={`p-4 rounded-lg bg-${insight.color}-50 border border-${insight.color}-200`}>
                      <div className="flex items-start gap-3">
                        <insight.icon className={`text-${insight.color}-600`} size={20} />
                        <div>
                          <p className={`font-semibold text-${insight.color}-800`}>{insight.title}</p>
                          <p className={`text-sm text-${insight.color}-700`}>{insight.message}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Best Performer */}
            {bestCategory && (
              <div className="card bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200">
                <div className="flex items-center gap-4">
                  <div className="p-4 bg-yellow-400 rounded-full">
                    <Award className="text-white" size={32} />
                  </div>
                  <div>
                    <p className="text-sm text-yellow-700">🏆 Best Performing Quality Category</p>
                    <p className="text-xl font-bold text-yellow-800">{mapQualityName(bestCategory.category)}</p>
                    <p className="text-sm text-yellow-700">
                      {formatPercent(bestCategory.gain_percent)} return • {formatCurrency(bestCategory.gain)} profit
                    </p>
                  </div>
                </div>
              </div>
            )}

            {(!summary || summary.total_market_value === 0) && (
              <div className="card text-center py-12">
                <BarChart3 className="mx-auto text-gray-400 mb-4" size={48} />
                <h3 className="text-lg font-semibold text-gray-700">No Performance Data Yet</h3>
                <p className="text-gray-500 mt-2">
                  Upload your Cheatsheet.xlsx file from the Bulk Upload page to see your portfolio performance analytics.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Gains by Quality Tab */}
        {activeTab === 'quality' && (
          <div className="space-y-6">
            {loadingQuality ? (
              <div className="text-center py-12">
                <RefreshCw className="animate-spin mx-auto mb-4" size={32} />
                <p className="text-gray-500">Loading quality analysis...</p>
              </div>
            ) : !qualityData ? (
              <div className="text-center py-12 text-gray-500">
                <BarChart3 size={48} className="mx-auto mb-4 opacity-50" />
                <p>No data available. Please ensure you have holdings data.</p>
              </div>
            ) : (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="card bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                    <p className="text-sm text-blue-600 uppercase tracking-wider mb-1">Total Portfolio Value</p>
                    <p className="text-2xl font-bold text-blue-900">{formatCurrency(qualityData.total_portfolio_value)}</p>
                  </div>
                  <div className="card bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                    <p className="text-sm text-green-600 uppercase tracking-wider mb-1">Total Invested</p>
                    <p className="text-2xl font-bold text-green-900">{formatCurrency(qualityData.total_invested)}</p>
                  </div>
                  <div className="card bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                    <p className="text-sm text-purple-600 uppercase tracking-wider mb-1">Total Companies</p>
                    <p className="text-2xl font-bold text-purple-900">{qualityData.total_holdings_count}</p>
                  </div>
                </div>

                {/* Quality Breakdown Table */}
                <div className="card overflow-hidden">
                  <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                    <BarChart3 className="text-primary" size={20} />
                    Gains by Quality Category
                  </h3>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="text-left py-3 px-4 font-semibold">Quality</th>
                          <th className="text-right py-3 px-4 font-semibold">Value</th>
                          <th className="text-right py-3 px-4 font-semibold">Gain %</th>
                          <th className="text-right py-3 px-4 font-semibold">% of Portfolio</th>
                          <th className="text-center py-3 px-4 font-semibold">Holdings</th>
                        </tr>
                      </thead>
                      <tbody>
                        {qualityData.qualities.filter(q => q.holdings_count > 0).map((quality, idx) => (
                          <tr key={quality.quality} className={`border-b hover:bg-gray-50 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                            <td className="py-3 px-4 font-medium">{quality.quality}</td>
                            <td className="text-right py-3 px-4">{formatCurrency(quality.total_value)}</td>
                            <td className={`text-right py-3 px-4 font-medium ${quality.gain_percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {quality.gain_percent >= 0 ? '+' : ''}{quality.gain_percent.toFixed(1)}%
                            </td>
                            <td className="text-right py-3 px-4">{quality.percent_of_portfolio.toFixed(1)}%</td>
                            <td className="text-center py-3 px-4">{quality.holdings_count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Value Range Summary Card - Clickable */}
                <div className="card">
                  <h3 className="font-bold text-lg mb-4">Value by Range (All Qualities Combined)</h3>
                  <p className="text-sm text-gray-500 mb-4">Click on a card to see company details</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {qualityData.value_range_labels?.map(label => {
                      const totalValue = qualityData.qualities.reduce((sum, q) => {
                        const rangeData = q.value_ranges?.find(vr => vr.range === label);
                        return sum + (rangeData?.value || 0);
                      }, 0);
                      const totalCount = qualityData.qualities.reduce((sum, q) => {
                        const rangeData = q.value_ranges?.find(vr => vr.range === label);
                        return sum + (rangeData?.count || 0);
                      }, 0);
                      const percentOfPortfolio = qualityData.total_portfolio_value > 0 
                        ? (totalValue / qualityData.total_portfolio_value * 100) 
                        : 0;
                      
                      return (
                        <div 
                          key={label} 
                          className="p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 hover:shadow-md transition-all border-2 border-transparent hover:border-primary/30"
                          onClick={() => {
                            setSelectedRange(label);
                            setShowRangeModal(true);
                          }}
                        >
                          <p className="text-xs text-gray-500 uppercase font-medium">{label}</p>
                          <p className="text-lg font-bold text-gray-800">{formatCurrency(totalValue)}</p>
                          <p className="text-xs text-gray-500">
                            {totalCount} companies • {percentOfPortfolio.toFixed(1)}% of portfolio
                          </p>
                          <p className="text-xs text-primary mt-1">Click for details →</p>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Holdings by Value Range (per Quality) */}
                <div className="card">
                  <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                    <PieChart className="text-primary" size={20} />
                    Holdings by Value Range (per Quality)
                  </h3>
                  <p className="text-sm text-gray-500 mb-4">
                    Shows number of companies in each value range and their percentage of total holdings
                  </p>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="text-left py-2 px-2 font-semibold sticky left-0 bg-gray-100">Quality</th>
                          {qualityData.value_range_labels?.map(label => (
                            <th key={label} className="text-center py-2 px-2 font-semibold min-w-[80px]">
                              <div>{label}</div>
                              <div className="text-gray-400 font-normal">#  |  %</div>
                            </th>
                          ))}
                          <th className="text-right py-2 px-2 font-semibold">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {qualityData.qualities.filter(q => q.holdings_count > 0).map((quality, idx) => (
                          <tr key={quality.quality} className={`border-b ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                            <td className="py-2 px-2 font-medium sticky left-0 bg-inherit">{quality.quality}</td>
                            {quality.value_ranges?.map(vr => (
                              <td key={vr.range} className="text-center py-2 px-2">
                                {vr.count > 0 ? (
                                  <div>
                                    <span className="font-medium">{vr.count}</span>
                                    <span className="text-gray-400 mx-1">|</span>
                                    <span className="text-gray-500">{vr.percent_of_total_holdings.toFixed(1)}%</span>
                                  </div>
                                ) : (
                                  <span className="text-gray-300">-</span>
                                )}
                              </td>
                            ))}
                            <td className="text-right py-2 px-2 font-semibold">{quality.holdings_count}</td>
                          </tr>
                        ))}
                        {/* Total Row */}
                        <tr className="bg-gray-200 font-semibold">
                          <td className="py-2 px-2 sticky left-0 bg-gray-200">TOTAL</td>
                          {qualityData.value_range_labels?.map(label => {
                            const totalInRange = qualityData.qualities.reduce((sum, q) => {
                              const rangeData = q.value_ranges?.find(vr => vr.range === label);
                              return sum + (rangeData?.count || 0);
                            }, 0);
                            const percentOfTotal = qualityData.total_holdings_count > 0 
                              ? (totalInRange / qualityData.total_holdings_count * 100) 
                              : 0;
                            return (
                              <td key={label} className="text-center py-2 px-2">
                                {totalInRange > 0 ? (
                                  <div>
                                    <span>{totalInRange}</span>
                                    <span className="text-gray-500 mx-1">|</span>
                                    <span>{percentOfTotal.toFixed(1)}%</span>
                                  </div>
                                ) : '-'}
                              </td>
                            );
                          })}
                          <td className="text-right py-2 px-2">{qualityData.total_holdings_count}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Value Range Detail Modal */}
                <Dialog open={showRangeModal} onOpenChange={setShowRangeModal}>
                  <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <BarChart3 className="text-primary" size={24} />
                        Companies in {selectedRange} Range
                      </DialogTitle>
                    </DialogHeader>
                    
                    {selectedRange && qualityData.companies_by_range?.[selectedRange] && (
                      <div className="mt-4">
                        <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                          <p className="text-sm text-blue-800">
                            <strong>{qualityData.companies_by_range[selectedRange].length}</strong> companies with total value of{' '}
                            <strong>{formatCurrency(qualityData.companies_by_range[selectedRange].reduce((sum, c) => sum + c.current_value, 0))}</strong>
                          </p>
                        </div>
                        
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="bg-gray-100">
                                <th className="text-left py-2 px-3 font-semibold">Symbol</th>
                                <th className="text-left py-2 px-3 font-semibold">Quality</th>
                                <th className="text-right py-2 px-3 font-semibold">Qty</th>
                                <th className="text-right py-2 px-3 font-semibold">Invested</th>
                                <th className="text-right py-2 px-3 font-semibold">Current Value</th>
                                <th className="text-right py-2 px-3 font-semibold">Gain</th>
                                <th className="text-right py-2 px-3 font-semibold">Gain %</th>
                              </tr>
                            </thead>
                            <tbody>
                              {qualityData.companies_by_range[selectedRange].map((company, idx) => (
                                <tr key={company.symbol} className={`border-b ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                                  <td className="py-2 px-3 font-medium">{company.symbol}</td>
                                  <td className="py-2 px-3">
                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                      company.quality === 'High' ? 'bg-green-100 text-green-800' :
                                      company.quality === 'Moderate' ? 'bg-blue-100 text-blue-800' :
                                      company.quality === 'Low' ? 'bg-yellow-100 text-yellow-800' :
                                      company.quality === 'New' ? 'bg-purple-100 text-purple-800' :
                                      'bg-gray-100 text-gray-800'
                                    }`}>
                                      {company.quality}
                                    </span>
                                  </td>
                                  <td className="text-right py-2 px-3">{company.quantity.toLocaleString()}</td>
                                  <td className="text-right py-2 px-3">{formatCurrency(company.invested)}</td>
                                  <td className="text-right py-2 px-3 font-medium">{formatCurrency(company.current_value)}</td>
                                  <td className={`text-right py-2 px-3 ${company.gain >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {formatCurrency(company.gain)}
                                  </td>
                                  <td className={`text-right py-2 px-3 font-medium ${company.gain_percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {company.gain_percent >= 0 ? '+' : ''}{company.gain_percent.toFixed(1)}%
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr className="bg-gray-200 font-semibold">
                                <td className="py-2 px-3" colSpan="3">TOTAL</td>
                                <td className="text-right py-2 px-3">
                                  {formatCurrency(qualityData.companies_by_range[selectedRange].reduce((sum, c) => sum + c.invested, 0))}
                                </td>
                                <td className="text-right py-2 px-3">
                                  {formatCurrency(qualityData.companies_by_range[selectedRange].reduce((sum, c) => sum + c.current_value, 0))}
                                </td>
                                <td className="text-right py-2 px-3">
                                  {formatCurrency(qualityData.companies_by_range[selectedRange].reduce((sum, c) => sum + c.gain, 0))}
                                </td>
                                <td className="text-right py-2 px-3">
                                  {(() => {
                                    const totalInvested = qualityData.companies_by_range[selectedRange].reduce((sum, c) => sum + c.invested, 0);
                                    const totalGain = qualityData.companies_by_range[selectedRange].reduce((sum, c) => sum + c.gain, 0);
                                    const pct = totalInvested > 0 ? (totalGain / totalInvested * 100) : 0;
                                    return `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`;
                                  })()}
                                </td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </div>
                    )}
                    
                    {selectedRange && (!qualityData.companies_by_range?.[selectedRange] || qualityData.companies_by_range[selectedRange].length === 0) && (
                      <div className="text-center py-8 text-gray-500">
                        No companies in this value range
                      </div>
                    )}
                  </DialogContent>
                </Dialog>
              </>
            )}
          </div>
        )}

        {/* By Quality Tab */}
        {activeTab === 'investments' && (
          <div className="space-y-6">
            <div className="card">
              <h3 className="font-bold text-lg mb-4">Portfolio Breakdown by Quality Category</h3>
              <p className="text-sm text-gray-500 mb-4">
                Data Source: Cheatsheet.xlsx → Value & Investments tab
              </p>
              {investments.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No investment data available. Upload Cheatsheet.xlsx to see breakdown.</p>
              ) : (
                <div className="space-y-4">
                  {investments.map((inv, idx) => (
                    <div key={idx} className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-semibold text-lg">{mapQualityName(inv.category)}</span>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          inv.gain_percent >= 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {formatPercent(inv.gain_percent)}
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500">Market Value</p>
                          <p className="font-medium text-green-600">{formatCurrency(inv.market_value)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Amount Invested</p>
                          <p className="font-medium">{formatCurrency(inv.amount_invested)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Gain/Loss</p>
                          <p className={`font-medium ${inv.gain >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(inv.gain)}
                          </p>
                        </div>
                      </div>
                      <div className="mt-3">
                        <div className="flex justify-between text-xs text-gray-500 mb-1">
                          <span>% of Portfolio</span>
                          <span>{inv.percent_of_total?.toFixed(2) || 0}%</span>
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-primary rounded-full"
                            style={{ width: `${Math.min((inv.percent_of_total || 0), 100)}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Monthly History Tab */}
        {activeTab === 'interest' && (
          <div className="space-y-6">
            <div className="card">
              <h3 className="font-bold text-lg mb-2">
                Performance History 
                <span className="text-sm font-normal text-gray-500 ml-2">
                  ({selectedPeriod === 'day' ? 'Today' : selectedPeriod === 'week' ? 'Last 7 Days' : selectedPeriod === 'month' ? 'Last 30 Days' : selectedPeriod === 'year' ? 'Last 12 Months' : 'All Time'})
                </span>
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Data Source: Cheatsheet.xlsx → Interest tab • Showing {filteredSnapshots.length} record{filteredSnapshots.length !== 1 ? 's' : ''} for selected period
              </p>
              {filteredSnapshots.length === 0 ? (
                <div className="text-center py-8">
                  <Calendar className="mx-auto text-gray-300 mb-4" size={48} />
                  <p className="text-gray-500">No performance history available for this period.</p>
                  <p className="text-sm text-gray-400 mt-2">Try selecting a longer time period or upload more data.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left py-3 px-4">Date</th>
                        <th className="text-right py-3 px-4">Portfolio Value</th>
                        <th className="text-right py-3 px-4">Addition</th>
                        <th className="text-right py-3 px-4">Interest Paid</th>
                        <th className="text-right py-3 px-4">Net Change</th>
                        <th className="text-right py-3 px-4">Your Return</th>
                        <th className="text-right py-3 px-4">Sensex</th>
                        <th className="text-right py-3 px-4">Nifty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...filteredSnapshots].sort((a, b) => new Date(b.date) - new Date(a.date)).map((snap, idx) => (
                        <tr key={idx} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-4 font-medium">
                            {new Date(snap.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </td>
                          <td className="py-3 px-4 text-right font-medium">
                            {formatCurrency(snap.portfolio_value)}
                          </td>
                          <td className="py-3 px-4 text-right text-blue-600">
                            {snap.addition ? formatCurrency(snap.addition) : '-'}
                          </td>
                          <td className="py-3 px-4 text-right text-red-600">
                            {snap.interest_paid ? formatCurrency(snap.interest_paid) : '-'}
                          </td>
                          <td className={`py-3 px-4 text-right ${(snap.net_increase || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(snap.net_increase || 0)}
                          </td>
                          <td className={`py-3 px-4 text-right font-medium ${(snap.net_increase_percent || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatPercent(snap.net_increase_percent || 0)}
                          </td>
                          <td className={`py-3 px-4 text-right ${(snap.sensex_change_percent || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatPercent(snap.sensex_change_percent || 0)}
                          </td>
                          <td className={`py-3 px-4 text-right ${(snap.nifty_change_percent || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatPercent(snap.nifty_change_percent || 0)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Dividends Tab */}
        {activeTab === 'dividends' && (
          <div className="space-y-6">
            {/* Add / Bulk Import Action Bar */}
            <div className="flex items-center justify-between flex-wrap gap-3" data-testid="dividends-actions">
              <div>
                <h2 className="text-lg font-bold text-gray-800">Dividend Records</h2>
                <p className="text-xs text-gray-500">Periodic entry, bulk import, or yearly summary</p>
              </div>
              <button
                onClick={() => navigate('/bulk-dividends')}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-md text-sm font-medium flex items-center gap-1"
                data-testid="open-bulk-dividends-btn"
              >
                <IndianRupee size={14} /> Add / Bulk Import Dividends
              </button>
            </div>

            {/* Summary Card */}
            {summary && summary.total_dividends > 0 && (
              <div className="card bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200">
                <div className="flex items-center gap-4">
                  <div className="p-4 bg-green-500 rounded-full">
                    <IndianRupee className="text-white" size={32} />
                  </div>
                  <div>
                    <p className="text-sm text-green-700">Total Dividends Earned</p>
                    <p className="text-2xl font-bold text-green-800">{formatCurrency(summary.total_dividends)}</p>
                    <p className="text-sm text-green-600">
                      Yield: {summary.total_invested > 0 ? `${(summary.total_dividends / summary.total_invested * 100).toFixed(2)}%` : '0%'} on total investment
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="card">
              <h3 className="font-bold text-lg mb-2">Dividend History</h3>
              <p className="text-sm text-gray-500 mb-4">
                Data Source: Cheatsheet.xlsx → Dividend tab or separate dividend uploads
              </p>
              
              {dividendData.length === 0 || dividendData.every(d => !d.dividend_received || d.dividend_received === 0) ? (
                <div className="text-center py-8">
                  <IndianRupee className="mx-auto text-gray-300 mb-4" size={48} />
                  <p className="text-gray-500">No dividend records found.</p>
                  <p className="text-sm text-gray-400 mt-2">
                    Dividend data can be uploaded via the Bulk Upload page using your Cheatsheet.xlsx file or a separate Tally export.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left py-3 px-4">Date</th>
                        <th className="text-right py-3 px-4">Portfolio Value</th>
                        <th className="text-right py-3 px-4">New Investment</th>
                        <th className="text-right py-3 px-4">Dividend Received</th>
                        <th className="text-right py-3 px-4">Dividend %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dividendData
                        .filter(div => div.dividend_received && div.dividend_received > 0)
                        .sort((a, b) => new Date(b.date) - new Date(a.date))
                        .slice(0, 50)
                        .map((div, idx) => (
                          <tr key={idx} className="border-b hover:bg-gray-50">
                            <td className="py-3 px-4 font-medium">
                              {new Date(div.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                            </td>
                            <td className="py-3 px-4 text-right">{formatCurrency(div.portfolio_value)}</td>
                            <td className="py-3 px-4 text-right text-blue-600">
                              {div.new_investment ? formatCurrency(div.new_investment) : '-'}
                            </td>
                            <td className="py-3 px-4 text-right text-green-600 font-medium">
                              {formatCurrency(div.dividend_received)}
                            </td>
                            <td className="py-3 px-4 text-right text-purple-600">
                              {div.dividend_percent_of_portfolio?.toFixed(2) || 0}%
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                  
                  {dividendData.filter(div => div.dividend_received && div.dividend_received > 0).length === 0 && (
                    <p className="text-center text-gray-500 py-4">No dividend payments recorded yet.</p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Calculation Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HelpCircle className="text-primary" size={24} />
              {calculationDetail?.title}
            </DialogTitle>
          </DialogHeader>
          
          {calculationDetail && (
            <div className="space-y-4">
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-sm font-medium text-blue-700">Formula:</p>
                <p className="text-blue-900 font-mono">{calculationDetail.formula}</p>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Calculation Breakdown:</p>
                <div className="space-y-2">
                  {calculationDetail.breakdown.map((item, idx) => (
                    <div 
                      key={idx} 
                      className={`flex justify-between items-center p-2 rounded ${item.highlight ? 'bg-green-100 border border-green-300' : 'bg-gray-50'}`}
                    >
                      <div>
                        <span className={item.highlight ? 'font-bold' : ''}>{item.label}</span>
                        <span className="text-xs text-gray-500 ml-2">({item.source})</span>
                      </div>
                      <span className={`font-mono font-medium ${item.highlight ? 'text-green-700 text-lg' : ''}`}>
                        {item.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-3 bg-amber-50 rounded-lg">
                <p className="text-sm font-medium text-amber-700">Data Source:</p>
                <p className="text-amber-900">{calculationDetail.dataSource}</p>
              </div>

              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium text-gray-700">Explanation:</p>
                <p className="text-gray-600 text-sm">{calculationDetail.explanation}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Performance;
